import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ControlContainer, FormsModule, NgForm } from '@angular/forms';
import { MuiInputDirective } from '@df/legacy/ui/form/mui-input.directive';
import { OffClickModule } from '@df/module-off-click/off-click.module';
import { OrderByPipe } from '@df/ui/common/order-by.pipe';
import { AddressFieldsetComponentDef } from '@df/ui/form/address-fieldset.component.def';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { BehaviorSubject } from 'rxjs';
import { IfNotTradeStoreDirective } from '../../../multisite/trade/if-not-trade-store.directive';
import { CheckboxComponent } from '../checkbox';
import { InputWrapBodyDirective } from '../input-wrap-body.directive';
import { InputWrapComponent } from '../input-wrap.component';
import { PushCountriesToFrontPipe } from './push-countries-to-front.pipe';

declare module '@df/session/api/address-book/address' {
    interface IAddressData {
        dni?: string;
    }
}
declare module '@df/ui/form/address-searcher' {
    export interface IHint {
        Text?: string;
    }
}

export const NAME_PREFIXES = ['Mr', 'Mrs', 'Miss'];
@Component({
    selector: 'address-fieldset',
    templateUrl: 'address-fieldset.html',
    standalone: true,
    imports: [
        FormsModule,
        InputWrapComponent,
        InputWrapBodyDirective,
        TranslationModule,
        MuiInputDirective,
        OrderByPipe,
        PushCountriesToFrontPipe,
        OffClickModule,
        NgClass,
        CheckboxComponent,
        IfNotTradeStoreDirective
    ],
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }]
})
export class AddressFieldsetComponent extends AddressFieldsetComponentDef implements OnInit {
    phoneFocusMsg = "We'll only ever send you delivery updates";
    public prefixes = NAME_PREFIXES;
    @Input()
    hideFirstAndLastName: boolean;
    @Input()
    hidePhone: boolean;
    @Output()
    saveAddress = new EventEmitter();

    protected canValidatePostcode = false;
    protected readonly postcodeWarning$ = new BehaviorSubject<string | undefined>(undefined);

    override ngOnInit(): void {
        this.subscriptions.add(this.postcodeWarning$.subscribe(() => this.detectChanges()));
        this.subscriptions.add(this.form.ngSubmit?.subscribe(() => this.detectChanges()));
        super.ngOnInit();
    }
    get isCountrySpain(): boolean {
        return this.data.country_id === 'ES';
    }
    checkedChangeHandle(event: boolean): void {
        this.detectChanges();
    }
    onInputBlur(): void {
        this.saveAddress.emit();
    }

    override onSearcherSelectionApplied(): void {
        super.onSearcherSelectionApplied();
        this.saveAddress.emit();
    }

    override onCountryChange(): void {
        this.handlePostcodeChange();
        super.onCountryChange();
        this.resetAddress();
    }

    protected handlePostcodeChange() {
        if (this.canValidatePostcode) {
            let warning: string | undefined = undefined;
            if (this.data.country_id === 'GB') {
                const postcode = this.data.postcode;
                if (postcode) {
                    if (
                        !postcode.match(
                            /^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9][A-Za-z]?))))\s?[0-9][A-Za-z]{2})$/i
                        )
                    ) {
                        warning = 'Postcode does not seem to be valid. Please double check it.';
                    }
                }
            }
            if (this.postcodeWarning$.value !== warning) {
                this.postcodeWarning$.next(warning);
            }
        }
    }

    protected handlePostcodeBlur() {
        this.canValidatePostcode = true;
        this.handlePostcodeChange();
    }

    protected resetAddress() {
        this.searcher.query = '';
        this.data.street_1 = '';
        this.data.street_2 = '';
        this.data.city = '';
        this.data.region = '';
        this.data.postcode = '';
        this.detectChanges();
    }
}
